import { Controller } from '@hotwired/stimulus'

/**
 * Контроллер для копирования текста в буфер обмена.
 * Используется на странице клуба (адрес) и в модалке «Поделиться» (ссылка).
 *
 * Пример использования:
 * <div data-controller="copy">
 *   <p data-copy-target="source">Текст для копирования</p>
 *   <button data-action="click->copy#copy">Копировать</button>
 * </div>
 *
 * После успешного копирования кнопка получает класс `is-copied`, а её иконка
 * временно меняется на галочку (`#icon-check`) — визуальная подсказка «скопировано».
 * Через copiedDuration (по умолчанию 1800мс) иконка возвращается назад.
 */
export default class CopyController extends Controller {
  static targets = ['source']

  static values = {
    text: String,
    copiedDuration: { type: Number, default: 1800 }
  }

  async copy(event) {
    event.preventDefault()

    // Захватываем кнопку ДО await: после await event.currentTarget обнуляется браузером.
    const button = event.currentTarget

    const text = this.hasSourceTarget
      ? this.sourceTarget.textContent
      : this.textValue
    if (!text) return

    try {
      await navigator.clipboard.writeText(text)
      this.#showCopied(button)
    } catch (err) {
      console.error('Failed to copy text: ', err)
    }
  }

  disconnect() {
    if (this._revertTimer) clearTimeout(this._revertTimer)
  }

  // Временная подсказка «скопировано»: галочка вместо исходной иконки + класс is-copied.
  #showCopied(button) {
    if (!button) return

    const iconUse = button.querySelector('svg use')

    // Повторный клик до сброса — не затираем сохранённую исходную иконку.
    if (!this._revertTimer) {
      this._originalHref = iconUse ? iconUse.getAttribute('href') : null
    } else {
      clearTimeout(this._revertTimer)
    }

    if (iconUse) iconUse.setAttribute('href', '#icon-check')
    button.classList.add('is-copied')

    this._revertTimer = setTimeout(() => {
      if (iconUse) {
        if (this._originalHref === null) {
          iconUse.removeAttribute('href')
        } else {
          iconUse.setAttribute('href', this._originalHref)
        }
      }
      button.classList.remove('is-copied')
      this._revertTimer = null
    }, this.copiedDurationValue)
  }
}
